import { useEffect, useState } from 'react';

import { PageHeader } from '../components/PageHeader.js';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '../components/StatePanel.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import { api, ApiError, type AuditEntry } from '../lib/api.js';
import { AUDIT_ACTION_LABELS, auditAsset, auditLabel } from '../lib/audit-labels.js';

const ALL_ACTIONS_VALUE = '__all__';

const ACTIONS = [ALL_ACTIONS_VALUE, ...Object.keys(AUDIT_ACTION_LABELS).filter((a) => a !== 'dataset.create.bundle')];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

export function AuditPage(): JSX.Element {
  const [items, setItems] = useState<AuditEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState(ALL_ACTIONS_VALUE);
  const [userFilter, setUserFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState<number>(1);

  // Filter- oder Page-Size-Wechsel resettet auf Seite 1.
  useEffect(() => {
    setPage(1);
  }, [actionFilter, userFilter, pageSize]);

  function refresh(): void {
    setError(null);
    api
      .listAuditLog({
        action: actionFilter && actionFilter !== ALL_ACTIONS_VALUE ? actionFilter : undefined,
        user: userFilter || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((err: unknown) => {
        const msg = err instanceof ApiError ? `${err.code ?? 'error'}: ${err.message}` : String(err);
        setError(msg);
        setItems([]);
      });
  }

  useEffect(refresh, [actionFilter, userFilter, pageSize, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Protokoll (Audit-Log)"
        lead="Chronologie aller schreibenden Operationen im Katalog. Aufbewahrung laut Doku 2 Jahre."
        actions={
          <Badge variant="secondary" className="text-xs tabular-nums">
            {total} Einträge
          </Badge>
        }
      />

      <Card className="flex flex-wrap items-end gap-4 p-4">
        <div className="space-y-1.5">
          <Label htmlFor="audit-action">Aktion</Label>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger id="audit-action" className="w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTIONS.map((a) => (
                <SelectItem key={a} value={a}>
                  {a === ALL_ACTIONS_VALUE ? 'alle' : auditLabel(a)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="audit-user">Benutzer</Label>
          <Input
            id="audit-user"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            placeholder="z.B. admin@civiport.localhost"
            className="w-64"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="audit-page-size">Pro Seite</Label>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => setPageSize(Number(v))}
          >
            <SelectTrigger id="audit-page-size" className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {error ? <ErrorPanel title="Konnte Audit-Log nicht laden" message={error} /> : null}
      {items === null ? <LoadingPanel /> : null}
      {items && items.length === 0 ? <EmptyPanel illustration="generic">Keine Audit-Einträge.</EmptyPanel> : null}

      {items && items.length > 0 ? (
        <>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Zeit</TableHead>
                  <TableHead>Benutzer</TableHead>
                  <TableHead>Aktion</TableHead>
                  <TableHead>Objekt</TableHead>
                  <TableHead className="text-right">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <Row
                    key={e.id}
                    entry={e}
                    expanded={expandedId === e.id}
                    onToggle={() => setExpandedId(expandedId === e.id ? null : e.id)}
                  />
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <span className="tabular-nums">
              {rangeFrom}–{rangeTo} von {total}
            </span>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2"
                disabled={page <= 1}
                onClick={() => setPage(1)}
                aria-label="Erste Seite"
              >
                «
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                aria-label="Vorherige Seite"
              >
                ‹
              </Button>
              <span className="px-2 tabular-nums">
                {page} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Nächste Seite"
              >
                ›
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2"
                disabled={page >= totalPages}
                onClick={() => setPage(totalPages)}
                aria-label="Letzte Seite"
              >
                »
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function Row({
  entry,
  expanded,
  onToggle,
}: {
  entry: AuditEntry;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const hasDetails =
    entry.prompt || entry.generated_output || entry.execution_result || entry.validation_result;

  return (
    <>
      <TableRow>
        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
          {new Date(entry.occurred_at).toLocaleString('de-DE')}
        </TableCell>
        <TableCell>{entry.user_id}</TableCell>
        <TableCell>
          <div className="font-medium">{auditLabel(entry.action)}</div>
          <code className="text-[11px] text-muted-foreground">{entry.action}</code>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">{auditAsset(entry.asset_type, entry.asset_id)}</TableCell>
        <TableCell className="text-right">
          {hasDetails ? (
            <Button type="button" variant="ghost" size="sm" onClick={onToggle}>
              {expanded ? 'zu' : 'mehr'}
            </Button>
          ) : null}
        </TableCell>
      </TableRow>
      {expanded && hasDetails ? (
        <TableRow className="bg-muted/40">
          <TableCell colSpan={5} className="p-4 text-xs">
            {entry.prompt ? (
              <Section title="Prompt">
                <pre className="whitespace-pre-wrap">{entry.prompt}</pre>
              </Section>
            ) : null}
            {entry.generated_output ? (
              <Section title="Generated output">
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-foreground">
                  {entry.generated_output}
                </pre>
              </Section>
            ) : null}
            {entry.execution_result ? (
              <Section title="Execution result">
                <pre className="max-h-48 overflow-auto rounded bg-muted p-2 text-foreground">
                  {JSON.stringify(entry.execution_result, null, 2)}
                </pre>
              </Section>
            ) : null}
            {entry.validation_result ? (
              <Section title="Validation">
                <pre className="max-h-32 overflow-auto rounded bg-muted p-2 text-foreground">
                  {JSON.stringify(entry.validation_result, null, 2)}
                </pre>
              </Section>
            ) : null}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="mb-2 last:mb-0">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{title}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
